import React, { memo, useMemo, useRef, useCallback, useEffect } from 'react';
import { Block, ChiffrageView, isPrestation } from '../../types';
import { BlockRow, LotEventItem } from '../molecules';
import { VersionEvent } from '../../types/Version';
import { DecompositionParams } from '../../services';

interface BlockListProps {
  blocks: Block[];
  allBlocks?: Block[];
  selectedBlocks: Set<string>;
  editingBlockId?: string;
  draggedBlocks: string[];
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Block>) => void;
  onDelete: (id: string) => void;
  onDragStart: (id: string, event: React.MouseEvent<Element>) => void;
  onDragEnd: () => void;
  onStopEditing: () => void;
  onToggleSelection: (id: string) => void;
  onRangeSelect?: (ids: string[]) => void;
  onSelectAll?: () => void;
  onClearSelection?: () => void;
  isBlockDragging: (blockId: string) => boolean;
  onToggleCollapse?: (id: string) => void;
  consultationMap?: Map<string, { name: string; id: string }>;
  onOpenConsultation?: (consultationId: string) => void;
  ventilationExpandedIds?: Set<string>;
  onToggleVentilation?: (id: string) => void;
  onVentilationBlocksChange?: (blocks: Block[]) => void;
  decompositionExpandedIds?: Set<string>;
  onToggleDecomposition?: (id: string) => void;
  decompositionParams?: DecompositionParams;
  chiffrageView?: ChiffrageView;
  events?: VersionEvent[];
  onPointEvent?: (eventId: string, lotId: string) => void;
  onOpenCreateEvent?: () => void;
}

// Composant mémorisé pour un bloc individuel
const BlockItem = memo<{
  block: Block;
  isSelected: boolean;
  isEditing: boolean;
  isDragging: boolean;
  allBlocks?: Block[];
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Block>) => void;
  onDelete: (id: string) => void;
  onDragStart: (id: string, event: React.MouseEvent<Element>) => void;
  onDragEnd: () => void;
  onStopEditing: () => void;
  onToggleSelection: (id: string, shiftKey: boolean) => void;
  onToggleCollapse?: (id: string) => void;
  consultationInfo?: { name: string; id: string; status?: string; retainedCompanyName?: string } | null;
  onOpenConsultation?: (consultationId: string) => void;
  isVentilationExpanded?: boolean;
  onToggleVentilation?: (id: string) => void;
  onVentilationBlocksChange?: (blocks: Block[]) => void;
  isDecompositionExpanded?: boolean;
  onToggleDecomposition?: (id: string) => void;
  decompositionParams?: DecompositionParams;
  chiffrageView?: ChiffrageView;
  lotEvents?: LotEventItem[];
  onPointEvent?: (eventId: string) => void;
  onOpenCreateEvent?: () => void;
}>(({
  block,
  isSelected,
  isEditing,
  isDragging,
  allBlocks,
  onSelect,
  onEdit,
  onUpdate,
  onDelete,
  onDragStart,
  onDragEnd,
  onStopEditing,
  onToggleSelection,
  onToggleCollapse,
  consultationInfo,
  onOpenConsultation,
  isVentilationExpanded,
  onToggleVentilation,
  onVentilationBlocksChange,
  isDecompositionExpanded,
  onToggleDecomposition,
  decompositionParams,
  chiffrageView,
  lotEvents,
  onPointEvent,
  onOpenCreateEvent,
}) => {
  return (
    <div
      data-block-id={block.id}
      className={`transition-opacity ${isDragging ? 'opacity-50' : ''}`}
    >
      <BlockRow
        block={block}
        isSelected={isSelected}
        isEditing={isEditing}
        isDragging={isDragging}
        allBlocks={allBlocks}
        onSelect={onSelect}
        onEdit={onEdit}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onStopEditing={onStopEditing}
        onToggleSelection={onToggleSelection}
        onToggleCollapse={onToggleCollapse}
        consultationInfo={consultationInfo}
        onOpenConsultation={onOpenConsultation}
        isVentilationExpanded={isVentilationExpanded}
        onToggleVentilation={onToggleVentilation}
        onVentilationBlocksChange={onVentilationBlocksChange}
        isDecompositionExpanded={isDecompositionExpanded}
        onToggleDecomposition={onToggleDecomposition}
        decompositionParams={decompositionParams}
        chiffrageView={chiffrageView}
        lotEvents={lotEvents}
        onPointEvent={onPointEvent}
        onOpenCreateEvent={onOpenCreateEvent}
      />
    </div>
  );
});

BlockItem.displayName = 'BlockItem';

export const BlockList: React.FC<BlockListProps> = memo(({
  blocks,
  allBlocks,
  selectedBlocks,
  editingBlockId,
  draggedBlocks,
  onSelect,
  onEdit,
  onUpdate,
  onDelete,
  onDragStart,
  onDragEnd,
  onStopEditing,
  onToggleSelection,
  onRangeSelect,
  onSelectAll,
  onClearSelection,
  isBlockDragging,
  onToggleCollapse,
  consultationMap,
  onOpenConsultation,
  ventilationExpandedIds,
  onToggleVentilation,
  onVentilationBlocksChange,
  decompositionExpandedIds,
  onToggleDecomposition,
  decompositionParams,
  chiffrageView,
  events,
  onPointEvent,
  onOpenCreateEvent,
}) => {
  const lastClickedIdRef = useRef<string | null>(null);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const handleToggleSelection = useCallback((id: string, shiftKey: boolean) => {
    if (shiftKey && lastClickedIdRef.current && lastClickedIdRef.current !== id && onRangeSelect) {
      const flatIds = blocks.map(b => b.id);
      const fromIdx = flatIds.indexOf(lastClickedIdRef.current);
      const toIdx = flatIds.indexOf(id);
      if (fromIdx !== -1 && toIdx !== -1) {
        const [lo, hi] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
        onRangeSelect(flatIds.slice(lo, hi + 1));
        return;
      }
    }
    lastClickedIdRef.current = id;
    onToggleSelection(id);
  }, [blocks, onToggleSelection, onRangeSelect]);

  const selectedCount = selectedBlocks.size;
  const totalCount = blocks.length;
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleSelectAllClick = useCallback(() => {
    if (allSelected || someSelected) {
      onClearSelection?.();
    } else {
      onSelectAll?.();
    }
  }, [allSelected, someSelected, onSelectAll, onClearSelection]);

  const blocksWithMetadata = useMemo(() => {
    return blocks.map((block) => {
      const isLot = !isPrestation(block) && block.level === 0;
      const lotEvents: LotEventItem[] | undefined = isLot && events
        ? events
            .filter(e => e.lotIds === null || e.lotIds.includes(block.id))
            .map(e => ({
              id: e.id,
              name: e.name,
              description: e.description,
              pointed: !!e.pointages[block.id],
            }))
        : undefined;
      return {
        block,
        isSelected: selectedBlocks.has(block.id),
        isEditing: editingBlockId === block.id,
        isDragging: isBlockDragging(block.id),
        consultationInfo: consultationMap?.get(block.id) ?? null,
        isVentilationExpanded: ventilationExpandedIds?.has(block.id) ?? false,
        isDecompositionExpanded: decompositionExpandedIds?.has(block.id) ?? false,
        lotEvents,
      };
    });
  }, [blocks, selectedBlocks, editingBlockId, isBlockDragging, consultationMap, ventilationExpandedIds, decompositionExpandedIds, events]);

  return (
    <div>
      {/* Header colonnes */}
      <div className="flex items-center py-1.5 bg-gray-50 border-b-2 border-gray-200 text-xs font-medium text-gray-400 uppercase tracking-wider sticky top-0 z-10">
        <div className="w-8 flex-shrink-0 flex items-center justify-center pl-2">
          <input
            ref={selectAllCheckboxRef}
            type="checkbox"
            checked={allSelected}
            onChange={() => {}}
            onClick={handleSelectAllClick}
            className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1 cursor-pointer"
            title={allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          />
        </div>
        <div className="w-5 flex-shrink-0" />
        <div className="flex-1 min-w-0 pl-1">Désignation</div>
        <div className="w-14 flex-shrink-0 text-center">Unité</div>
        <div className="w-16 flex-shrink-0 text-right">Qté</div>
        <div className="w-28 flex-shrink-0 text-right pr-2">P.U.</div>
        <div className="w-14 flex-shrink-0 text-center" title="Coef multiplicateur ligne — appliqué au PU pour obtenir le total">Coef</div>
        <div className="w-28 flex-shrink-0 text-right pr-2">Total HT</div>
        <div className="w-8 flex-shrink-0" />
        <div className="w-8 flex-shrink-0" />
        <div className="w-8 flex-shrink-0" />
        <div className="w-14 flex-shrink-0" />
      </div>

      {blocksWithMetadata.map(({ block, isSelected, isEditing, isDragging, consultationInfo, isVentilationExpanded, isDecompositionExpanded, lotEvents }) => (
        <BlockItem
          key={block.id}
          block={block}
          isSelected={isSelected}
          isEditing={isEditing}
          isDragging={isDragging}
          allBlocks={allBlocks}
          onSelect={onSelect}
          onEdit={onEdit}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onStopEditing={onStopEditing}
          onToggleSelection={handleToggleSelection}
          onToggleCollapse={onToggleCollapse}
          consultationInfo={consultationInfo}
          onOpenConsultation={onOpenConsultation}
          isVentilationExpanded={isVentilationExpanded}
          onToggleVentilation={onToggleVentilation}
          onVentilationBlocksChange={onVentilationBlocksChange}
          isDecompositionExpanded={isDecompositionExpanded}
          onToggleDecomposition={onToggleDecomposition}
          decompositionParams={decompositionParams}
          chiffrageView={chiffrageView}
          lotEvents={lotEvents}
          onPointEvent={lotEvents ? (eventId) => onPointEvent?.(eventId, block.id) : undefined}
          onOpenCreateEvent={onOpenCreateEvent}
        />
      ))}
    </div>
  );
});

BlockList.displayName = 'BlockList';
